/** The different dimensions that can vary in the UI */
export const Variants = {
    color: {
        values: ['light', 'dark'],
        default: 'light',
    },
    contrast: {
        values: ['high', 'low'],
        default: 'high',
    },
    radius: {
        values: ['rounded', 'straight'],
        default: 'rounded',
    },
} as const;

const VariantsKeys = Object.keys(Variants) as Array<keyof typeof Variants>;

export type VariantsValue = {
    [k in keyof typeof Variants]: (typeof Variants)[k]['values'][number];
};

export type KeyedVariantsValue = string;

/**
 * Transform a unique set of variants into a key that can be used as a cache key.
 */
export function keyVariantsValue(value: VariantsValue): KeyedVariantsValue {
    return `{${Object.keys(value)
        .sort()
        .map((k) => `"${k}":"${value[k]}"`)
        .join(',')}}`;
}

/**
 * Transform a variant cache key in a set of variants.
 */
export function deKeyVariantsValue(key: KeyedVariantsValue): VariantsValue {
    return JSON.parse(key);
}

/**
 * Resolve the variants of the theme for a given set of variants.
 */
function unpackVariants<T extends {}, U>(x: T, variants: VariantsValue): U {
    // @ts-ignore
    const result: U = {};

    const variantKeys = Object.keys(variants);

    Object.keys(x).forEach((key) => {
        const value = x[key];
        if (typeof value !== 'object' || typeof value === 'function') {
            result[key] = value;
        } else if ('value' in value) {
            let override;
            if (value.overrides) {
                override = value.overrides.find((option) => {
                    for (const key of variantKeys) {
                        if (typeof option[key] !== 'undefined' && option[key] !== variants[key]) {
                            return false;
                        }
                    }

                    return true;
                });
            }

            if (override) {
                result[key] = override.value;
            } else {
                result[key] = value.value;
            }
        } else {
            result[key] = unpackVariants(value, variants);
        }
    });

    return result;
}

export function createThemeMap<T extends {}, U>(theme: T) {
    const themeMap = new Map<KeyedVariantsValue, U>();

    forEachVariants((variant, keyedVariant) => {
        themeMap.set(keyedVariant, unpackVariants<T, U>(theme, variant));
    });

    return themeMap;
}

// Construct the whole variants map once
type VariantsMap = { [key: KeyedVariantsValue]: VariantsValue };
const variantsMap: VariantsMap = createVariantsMap();

function createVariantsMap(): VariantsMap {
    const result: VariantsMap = {};

    const forEachVariantsWith = (accu: Partial<VariantsValue>) => {
        const existingKeys = new Set(Object.keys(accu));
        const remainingKeys = VariantsKeys.filter((k) => !existingKeys.has(k));

        if (remainingKeys.length === 0) {
            result[keyVariantsValue(accu as VariantsValue)] = accu as VariantsValue;
        }

        remainingKeys.forEach((key) => {
            Variants[key].values.forEach((value) => {
                forEachVariantsWith({ ...accu, [key]: value });
            });
        });
    };

    forEachVariantsWith({});

    return result;
}

/**
 * Iterate over all possible variants of the theme.
 */
export function forEachVariants(
    fn: (variant: VariantsValue, keyedVariant: KeyedVariantsValue) => void
) {
    Object.entries(variantsMap).forEach(([key, value]) => fn(value, key));
}

/**
 * Return the default variants value to use based on the variants spec.
 */
export function getDefaultVariantsValue(): VariantsValue {
    // @ts-ignore - we are initializing the value
    const result: VariantsValue = {};
    Object.entries(Variants).forEach(([key, value]) => {
        result[key] = value.default;
    });

    return result;
}
